import { Icon } from "armstrong-react";
import classNames from "classnames";
import React from "react";
import { Link } from "react-router";
import _ from "underscore";

import * as DateUtil from "@travellocal/utils/dateHelpers";
import { Avatar, FlowBox, LinkButton, PriceInCurrency, ItineraryMeta } from "../../../../../core/atoms/atoms";
import { GeneratePdfButton } from "../../../../../shared/components/generatePdfButton";
import { GoogleAnalyticsHelper } from "@travellocal/ui";
import { arrayToSentence } from "../../../../../shared/helpers/stringHelpers";
import { ItinerarySummaryView } from "../../../../../shared/itinerarySummaryView";
import { MapWrapper } from "../../../../../shared/organisms/mapboxMap";
import { ShowMoreHeightFade } from "@travellocal/ui";
import { CustomerPaymentSummary } from "../../../../components/customer/customerPaymentSummary";
import { RoutesInvoker } from "../../../../routesInvoker";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import { isEnquiryBooked } from "../utils";
import { ItineraryNotesContent } from "./itineraryNotes";

import "./oldItinerary.scss";

interface Props {
  itinerary: IEnquiryItinerarySummary;
  enquiry: IEnquiryOutput;
  payments: IPaymentsOutput;
  lto: ILto;
}

const getItineraryEndDate = (itinerary: IEnquiryItinerarySummary) =>
  DateUtil.addDays(itinerary.startDate, itinerary.days.length - 1);

/**
 * Itinerary component for old sidebar layout
 */
export const OldItinerary: React.FC<Props> = ({ itinerary, enquiry, payments, lto, children }) => {

  const hasItinerary = enquiry.isConnected
    && enquiry.currentQuoteId != null
    && enquiry.currentItinerarySummary != null;

  if (!hasItinerary) {
    return (
      <div className="customer-enquiry-itinerary">
        <section className="width-wrapper itinerary-pending-message">
          <p>There&apos;s no itinerary to show right now.</p>
          <p><Link to={RoutesInvoker.customerIndex}>Back to Home</Link></p>
        </section>
      </div>
    );
  }

  // Only show the booking UI on the most recent itinerary
  const isCurrentItinerary = itinerary.quoteId === enquiry.currentQuoteId;
  const canBook = isCurrentItinerary && !isEnquiryBooked(enquiry);
  const sender = itinerary.ltoMessage.fromUser;

  const itineraryLocations = _.chain(itinerary.days)
    .map(day => day.locationNames)
    .flatten()
    .sortBy()
    .unique(true)
    .value();

  const locations = itinerary.geoLocations.length > 0
    ? itinerary.geoLocations
    : [itinerary.primaryLocation.geoLocation];

  // TODO geolocations == accomm locations might not be true in future
  const accomNames = _.unique(itinerary.days.map(x => x.dayAccommodation).filter(x => x != null).map(x => x.name));

  return (
    <div className="customer-enquiry-itinerary">

      {/* Pull in image gallery */}
      {children}

      <FlowBox
        className={classNames("itinerary-header width-wrapper", { "bookable": canBook, "not-bookable": !canBook })}
      >

        <ItineraryMeta
          isCurrentItinerary={isCurrentItinerary}
          sentTime={DateUtil.fromNow(itinerary.ltoMessage.sentTime)}
          author={itinerary.ltoMessage.fromUser.firstName}
          versionNumber={itinerary.version}
        />

        <FlowBox className="itinerary-summary">
          <div className="stat">
            <Icon icon={Icon.Icomoon.users} /> {itinerary.numberOfPeople} travellers
          </div>
          <div className="stat">
            <Icon icon={Icon.Icomoon.calendar2} /> {itinerary.days.length} days
          </div>
          <div className="stat">
            <Icon icon={Icon.Icomoon.airplane3} />{" "}
            {DateUtil.formatDate(itinerary.startDate, "ddd Do MMM")} -{" "}
            {DateUtil.formatDate(getItineraryEndDate(itinerary), "ddd Do MMM")}
          </div>
          <div className="stat">
            <Icon icon={Icon.Icomoon.cash2} />{" "}
            <PriceInCurrency priceInCurrency={itinerary.totalPrice} displayForCustomer={true} />
          </div>
        </FlowBox>

        {!isCurrentItinerary &&
          <div className="help-message">
            <p>
              If you&apos;d like to book this itinerary, please ask {itinerary.ltoMessage.fromUser.firstName}{" "}
              to reissue <strong>Itinerary #{itinerary.version}</strong>.
            </p>

            <LinkButton
              to={WebRoutes.customerEnquiryMessaging(enquiry.id)}
              icomoon={Icon.Icomoon.bubbles10}
              className="primary-button inverted"
            >
              Send a message
            </LinkButton>
            <LinkButton
              to={WebRoutes.customerEnquiryItinerary(enquiry.id, enquiry.currentQuoteId)}
              icomoon={Icon.Icomoon.map2}
              className="primary-button inverted"
            >
              View current itinerary
            </LinkButton>
          </div>
        }
      </FlowBox>

      <section className="lto-itinerary-message width-wrapper">
        <Avatar source={sender.image ? sender.image.regularUrl : null} />
        {itinerary.isExternal ? <h2>Your itinerary from {sender.firstName}</h2> : <h2>{sender.firstName} said...</h2>}
        <div className="message">
          <div className="bubble">
            <ShowMoreHeightFade
              threshold={200}
              onOpen={() => GoogleAnalyticsHelper.logEvent("Show detail", "Click", "Itinerary tab")}
            >
              <div dangerouslySetInnerHTML={{ __html: itinerary.ltoMessage.text }} />
            </ShowMoreHeightFade>
          </div>
        </div>
        {itinerary.isExternal && (
          <LinkButton
            icomoon={Icon.Icomoon.newTab}
            className="primary-button itinerary-button"
            to={itinerary.itineraryUrl}
            target="_blank"
            onClick={() =>
              GoogleAnalyticsHelper.logEvent(
                "External Itinerary",
                "Click",
                "Itinerary tab"
              )
            }
          >
            View your itinerary
          </LinkButton>
        )}
        <LinkButton
          icomoon={Icon.Icomoon.bubbles10}
          className="primary-button inverted"
          to={WebRoutes.customerEnquiryMessaging(enquiry.id)}
          onClick={() =>
            GoogleAnalyticsHelper.logEvent(
              "Reply",
              "Click",
              "Itinerary tab"
            )
          }
        >
          Reply to {sender.firstName}
        </LinkButton>
      </section>

      <FlowBox className="itinerary-body width-wrapper marketing-2cols-sidebar">
        <div className="main">
          <section className="itinerary-breakdown">
            {!itinerary.isExternal && (
              <>
                <header>
                  <h2>Day by day</h2>
                  <p className="subtitle">
                    Visiting {arrayToSentence(itineraryLocations)}
                  </p>
                </header>
                <div className="actions">
                  <GeneratePdfButton
                    itineraryId={itinerary.id}
                    gAEventLabel="Itinerary tab"
                  />
                </div>

                <ItinerarySummaryView itinerary={itinerary} lto={lto} />
              </>
            )}
          </section>

          <section>
            <h2>Important notes</h2>

            <ItineraryNotesContent
              enquiry={enquiry}
              itinerary={itinerary}
              lto={lto}
            />
          </section>
        </div>

        <div className="sidebar">
          <CustomerPaymentSummary
            audience={UserType.Customer}
            enquiry={enquiry}
            depositPaid={payments.payments.length !== 0}
            itineraryQuote={itinerary}
            lto={lto}
            isBookable={isCurrentItinerary}
            bookingRoute={WebRoutes.customerEnquiryMakePayment(enquiry.id)}
            balanceDueDays={itinerary.paymentTerms.balanceDueDays}
          />
        </div>
      </FlowBox>

      {itinerary.primaryLocation && !itinerary.isExternal && (
        <section>
          <div className="width-wrapper">
            <h2>Itinerary map</h2>
            {accomNames.length > 0 &&
              <p className="subtitle">Accommodation at {arrayToSentence(accomNames)}</p>
            }
          </div>
          <MapWrapper height={500} markerLocation={locations} />
        </section>
      )}
    </div>
  );
}

